import { useEffect, useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Avatar, isImgAvatar } from "./Avatar";

const IMG = "https://static.prod-images.emergentagent.com/jobs/bbbc05ed-34dc-430c-a8e1-f70aee71a63a/images/";
const ZODIAC = [
  ["鼠", "05b53263323d8cbf95d692e92b0c1beb9066ee6b784b01eef5891484543ebccd"], ["牛", "c760a920485496c426005d03fee1f30d924dc461ae2e59c07ed6985c86f706a4"],
  ["虎", "215eaced3c0d8900929cce3ff28cb138fb758f25955267441c5d0916b36c1878"], ["兔", "356df0f9885f40ab49fd5fb1e4202363fd3b3adef9e29efe050535d38f65ed5c"],
  ["龍", "d45f0aab3edd2f7ae8b928b55ef00add4884a3b635b6482b145d7ba87abd7786"], ["蛇", "2ca262c59748b0a0daa3556241b7c1c221888fdd6534b81794742b4d5b5d5050"],
  ["馬", "380db3a133dca8971797f8b8b5ccebbabd22b789aa88e5dc28a6ae288d259900"], ["羊", "0347f53ea106883a7ac24b1977ed4d4c78a4932e0630b364587a496b4d56acb8"],
  ["猴", "3cff36875300560377159b319f4d5cfc4511d0b67c1075953029aaecc1ced0e0"], ["雞", "4fa735b79f55e3983cdca03ba2ac0ba69cb87a26169085b03011b4d2f2ba99ae"],
  ["狗", "e915b58508a037ca893fed2b2970cf03a1c5d18beb237963a1fb049b1ae5f82b"], ["豬", "9f76b99b29dff44f8da0e3202c6373d486520a7d1c94f6d139ea0f62c83a5d6d"],
].map(([name, h]) => [name, `${IMG}${h}.jpeg`]);
const EMOJIS = ["🐼", "🦊", "🐸", "🐘", "🐱", "🐻", "🐨", "🦁", "🐧", "🧒", "👧", "🧑‍🎓"];
const BLANK = { nickname: "", avatar: ZODIAC[0][1], age: "5", learning_level: 1 };

const pick = (on) => `h-14 rounded-xl border-2 overflow-hidden flex items-center justify-center ${on ? "border-[var(--vermilion)] ring-2 ring-red-200" : "border-[var(--line)]"}`;

const PhotoUpload = ({ value, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(isImgAvatar(value) && value.startsWith("/api/") ? value : null);
  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) return toast.error("請選擇 5MB 以內的照片");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/children/avatar", fd);
      setPhoto(data.url);
      onChange(data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "上傳失敗，請再試一次");
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3 mt-2">
      {photo && <button type="button" onClick={() => onChange(photo)} className={`w-14 h-14 rounded-full border-2 overflow-hidden ${value === photo ? "border-[var(--vermilion)] ring-2 ring-red-200" : "border-[var(--line)]"}`} data-testid="avatar-photo-preview"><Avatar value={photo} /></button>}
      <button type="button" data-testid="avatar-upload-btn" onClick={() => ref.current.click()} disabled={busy}
        className="h-11 px-4 rounded-full border-2 border-dashed border-[var(--line)] flex items-center gap-2 text-sm font-bold text-stone-600">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {photo ? "Change photo" : "Upload a photo"}
      </button>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="avatar-upload-input" onChange={(e) => upload(e.target.files[0])} />
    </div>
  );
};

export const ChildForm = ({ open, onOpenChange, onSubmit, initial }) => {
  const [f, setF] = useState(BLANK);
  useEffect(() => {
    if (open) setF(initial ? { nickname: initial.nickname, avatar: initial.avatar, age: initial.age ?? "", learning_level: initial.learning_level || 1 } : BLANK);
  }, [open, initial]);
  const age = String(f.age).trim();
  const submit = () => onSubmit({ ...f, age: age === "" ? null : Number(age) });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="font-en max-h-[90vh] overflow-y-auto" data-testid="child-form-dialog">
        <DialogHeader><DialogTitle>{initial ? "Edit learner" : "New learner"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Nickname</Label>
            <Input data-testid="child-nickname-input" value={f.nickname} maxLength={20} onChange={(e) => setF({ ...f, nickname: e.target.value })} placeholder="e.g. 小寶" />
          </div>
          <div>
            <Label>Avatar · 十二生肖</Label>
            <div className="grid grid-cols-6 gap-2 mt-1" data-testid="avatar-zodiac-grid">
              {ZODIAC.map(([name, url]) => (
                <button key={name} type="button" title={name} data-testid={`avatar-zodiac-${name}`} onClick={() => setF({ ...f, avatar: url })} className={pick(f.avatar === url)}>
                  <img src={url} alt={name} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div className="grid grid-cols-6 gap-2 mt-2">
              {EMOJIS.map((a) => (
                <button key={a} type="button" data-testid={`avatar-option-${a}`} onClick={() => setF({ ...f, avatar: a })} className={`text-3xl ${pick(f.avatar === a)}`}>{a}</button>
              ))}
            </div>
            <PhotoUpload value={f.avatar} onChange={(url) => setF((x) => ({ ...x, avatar: url }))} />
          </div>
          <div>
            <Label>Age</Label>
            <Input data-testid="child-age-input" type="number" inputMode="numeric" value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} placeholder="e.g. 5" className="mt-1 w-32" />
          </div>
          <button data-testid="child-form-submit" disabled={!f.nickname.trim() || (age !== "" && !Number.isFinite(Number(age)))} onClick={submit}
            className="w-full h-12 rounded-full bg-[var(--vermilion)] text-white font-bold disabled:opacity-40">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
